import { APP_INITIALIZER, ApplicationConfig, isDevMode } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';
import { GANTT_GLOBAL_CONFIG, GANTT_I18N_LOCALE_TOKEN, enUsLocale } from '@worktile/gantt';
import Lara from '@primeuix/themes/lara';
import { definePreset } from '@primeuix/styled';

import { routes } from './app.routes';
import { jwtInterceptor } from './core/interceptors/jwt.interceptor';
import { apiResponseInterceptor } from './core/interceptors/api-response.interceptor';
import { NavigationValidationService } from './core/navigation/navigation-validation.service';

const FortarisPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '#e9eff1',
      100: '#d1dde1',
      200: '#afc2c9',
      300: '#8ca6b0',
      400: '#6a8b97',
      500: '#4b7281',
      600: '#356170',
      700: '#234e5a',
      800: '#173d46',
      900: '#0f2f36',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f7f9fa',
          100: '#eef2f4',
          200: '#e3e8eb',
          300: '#d6dde1',
          400: '#c4cdd3',
          500: '#9da9b2',
          600: '#6e7c86',
          700: '#4a565e',
          800: '#333c42',
          900: '#22282d',
        },
        primary: {
          color: '#123c4a',
          contrastColor: '#ffffff',
          hoverColor: '#173d46',
          activeColor: '#0f2f36',
        },
        highlight: {
          background: 'rgba(18, 60, 74, 0.12)',
          focusBackground: 'rgba(18, 60, 74, 0.16)',
          color: '#123c4a',
          focusColor: '#123c4a',
        },
        text: {
          color: '#2b2f33',
          mutedColor: 'rgba(43, 47, 51, 0.65)',
          hoverColor: '#2b2f33',
          hoverMutedColor: 'rgba(43, 47, 51, 0.75)',
        },
        formField: {
          background: '#ffffff',
          filledBackground: '#f7f9fa',
          borderColor: '#d9dde1',
          hoverBorderColor: '#c4cdd3',
          focusBorderColor: '#d97706',
          color: '#2b2f33',
          placeholderColor: 'rgba(43, 47, 51, 0.45)',
        },
        content: {
          background: '#ffffff',
          borderColor: '#d9dde1',
          color: '#2b2f33',
          hoverBackground: '#eef2f4',
          hoverColor: '#2b2f33',
        },
        overlay: {
          select: {
            background: '#ffffff',
            borderColor: '#d9dde1',
            color: '#2b2f33',
          },
          modal: {
            background: '#ffffff',
            borderColor: '#d9dde1',
            color: '#2b2f33',
          },
          popover: {
            background: '#ffffff',
            borderColor: '#d9dde1',
            color: '#2b2f33',
          },
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptors([jwtInterceptor, apiResponseInterceptor])),
    provideRouter(routes),
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [NavigationValidationService],
      useFactory: (service: NavigationValidationService) => () => {
        if (isDevMode()) {
          service.validar(routes);
        }
      },
    },
    MessageService,
    ConfirmationService,
    {
      provide: GANTT_GLOBAL_CONFIG,
      useValue: {
        locale: 'en-us',
        styleOptions: {
          headerHeight: 44,
          lineHeight: 44,
          barHeight: 28,
        },
      },
    },
    {
      provide: GANTT_I18N_LOCALE_TOKEN,
      useValue: enUsLocale,
      multi: true,
    },
    providePrimeNG({
      theme: {
        preset: FortarisPreset,
        options: {
          darkModeSelector: 'none',
        },
      },
    }),
  ]
};
